import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { StatCard } from "@/components/reports/stat-card";
import { BarChart } from "@/components/reports/bar-chart";

export const Route = createFileRoute("/_authenticated/reports/high-risk")({
  component: HighRiskReport,
});

interface HighRiskRow {
  highRiskType: string;
  projectName: string;
  count: number;
}

const HIGH_RISK_LABELS: Record<string, string> = {
  hot_work: "Hot Work",
  confined_space: "Confined Space",
  excavation: "Excavation",
  fall_protection: "Fall Protection",
  crane_lift: "Crane / Rigging",
  lockout: "Lockout / Tagout",
  other: "Other",
};

const HIGH_RISK_COLORS: Record<string, string> = {
  hot_work: "bg-orange-500",
  confined_space: "bg-purple-500",
  excavation: "bg-yellow-600",
  fall_protection: "bg-red-500",
  crane_lift: "bg-blue-500",
  lockout: "bg-teal-500",
  other: "bg-gray-500",
};

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function HighRiskReport() {
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get("dateFrom") || daysAgo(30);
  const dateTo = params.get("dateTo") || today();
  const projectId = params.get("projectId") || undefined;

  const qs = new URLSearchParams({ dateFrom, dateTo });
  if (projectId) qs.set("projectId", projectId);

  const { data, isLoading } = useQuery({
    queryKey: [
      "reports",
      "high-risk-frequency",
      { dateFrom, dateTo, projectId },
    ],
    queryFn: () =>
      apiGet<{ data: HighRiskRow[] }>(
        `/api/reports/high-risk-frequency?${qs.toString()}`
      ),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-20 animate-pulse bg-muted/40 rounded" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse bg-muted/40 rounded" />
        ))}
      </div>
    );
  }

  const rows = data?.data ?? [];
  const totalCount = rows.reduce((s, r) => s + r.count, 0);

  // Aggregate by type for the chart
  const byType = new Map<string, number>();
  for (const row of rows) {
    byType.set(row.highRiskType, (byType.get(row.highRiskType) || 0) + row.count);
  }

  const items = Array.from(byType.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({
      label: HIGH_RISK_LABELS[type] || type,
      value: count,
      color: HIGH_RISK_COLORS[type] || "bg-gray-500",
    }));

  return (
    <div className="space-y-4">
      <StatCard
        label="Total High-Risk Instances"
        value={totalCount}
        className={totalCount > 0 ? "border-orange-200 bg-orange-50/50" : undefined}
      />

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          High-Risk Work Types
        </h3>
        <BarChart
          items={items}
          emptyMessage="No high-risk work recorded for the selected period."
        />
      </div>

      {/* Project breakdown if no project filter */}
      {!projectId && rows.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            By Project
          </h3>
          <div className="border rounded-lg divide-y">
            {rows.map((row, i) => (
              <div
                key={`${row.highRiskType}-${row.projectName}-${i}`}
                className="flex items-center justify-between px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium">
                    {HIGH_RISK_LABELS[row.highRiskType] || row.highRiskType}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {row.projectName}
                  </span>
                </div>
                <span className="font-semibold tabular-nums shrink-0 ml-2">
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
